import React, { useContext, useEffect, useMemo, useRef } from 'react';

import checkMarkPrimarySvg from '@assets/img/icons/check-mark-primary.svg';
import {
  TagSearchInputFormControl,
  TagSearchInputStore,
} from '@components/Forms/TagSearchInputStore';
import { TextInput } from '@components/NextForms/TextInput';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Popper from '@material-ui/core/Popper';
import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import SearchIcon from '@material-ui/icons/Search';
import { getDecodeResult } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { type ClassMap, makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { pipe } from 'remeda';
import { z } from 'zod';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const strings = createStrings({
  ja: {},
});

const useStyles = makeMuiStyles((theme, common) => ({
  tagSearchInputContainer: {
    ...common.flexInline,
    padding: '0 8px',
    height: 32,
    border: `1px solid ${theme.palette.grey[500]}`,
    borderRadius: 3,
    backgroundColor: theme.palette.background.paper,
  },
  tagSearchInputPlaceholder: {
    ...common.middleText,
    ...common.textGray500,
    position: 'absolute',
    zIndex: 0,
    userSelect: 'none',
    width: '100%',
    '$focused &': {
      ...common.textGray700,
    },
  },
  tagSearchInputInputArea: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 8,
    flex: 1,
    position: 'relative',
    outline: 'none',
  },
  tagSearchInputInputAreaInner: {
    ...common.flexInline,
    position: 'relative',
    zIndex: 1,
  },
  tagSearchInputInput: {
    ...common.middleText,
    flex: 1,
    borderWidth: 0,
    outline: 'none',
    background: 'transparent',
  },
  tagSearchTagContainer: {
    ...common.flexInline,
    cursor: 'pointer',
    marginRight: '0.6rem',
  },
  searchTagAutoCompleteContainer: {
    ...common.middleText,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[300]}`,
    position: 'relative',
    top: 5,
  },
  searchTagAutoCompleteLabel: {
    ...common.flexInline,
    ...common.justifyBetween,
    ...common.itemsCenter,
    columnGap: '1rem',
    cursor: 'pointer',
    paddingBottom: '0.4rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    paddingTop: '0.4rem',
  },
  searchTagAutoCompleteLabelSelected: {
    ...common.backgroundPrimaryAlpha3,
  },
  focused: {},
}));

const TagSearchInputContext = React.createContext<TagSearchInputStore>({} as any);

interface TagSearchTagProps {
  name: string;
  creating?: boolean;
}

const TagSearchTag: React.FC<TagSearchTagProps> = observer(({ name, creating = false }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const store = useContext(TagSearchInputContext);

  const { value, errorMessages } = store.formControl.useField(name);

  const control = useMemo(() => store.fields[name], [name, store.fields]);

  return (
    <div
      className={classes.tagSearchTagContainer}
      style={{ marginRight: creating ? 0 : undefined }}
    >
      <CancelRoundedIcon
        className={classes.textGray500}
        style={{ fontSize: 17, marginRight: 3, position: 'relative', top: 2 }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!creating) {
            store.removeTag(name);
          } else {
            store.cancelTag();
          }
        }}
      />

      <span className={classes.textGray700}>{control.label}</span>
      {'：'}
      {!creating && (
        <span
          className={
            errorMessages.length > 0
              ? clsx(classes.textError, classes.fontWeight600)
              : classes.textPrimary
          }
        >
          {value}
        </span>
      )}
    </div>
  );
});

const SearchTagAutoComplete = observer(({ anchorEl }: { anchorEl?: HTMLDivElement | null }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const store = useContext(TagSearchInputContext);

  const open = store.state === 'SELECT_FIELD' && !!anchorEl;

  return (
    <Popper open={open} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1500 }}>
      <div className={classes.searchTagAutoCompleteContainer}>
        {Object.entries(store.fields).map(([name, field]) => (
          <a
            key={name}
            className={clsx(
              classes.searchTagAutoCompleteLabel,
              name === store.selectedAutoCompleteTag && classes.searchTagAutoCompleteLabelSelected,
            )}
            onMouseEnter={() => store.setSelectedAutoCompleteTag(name)}
            onClick={() => {
              store.startTag(name);
            }}
          >
            {field.label}

            <img
              src={checkMarkPrimarySvg}
              style={{
                height: 9,
                width: 9,
                opacity: name === store.selectedAutoCompleteTag ? 1 : 0,
              }}
            />
          </a>
        ))}
      </div>
    </Popper>
  );
});

const SearchTextForm = observer(() => {
  const store = useContext(TagSearchInputContext);

  const { currentField } = store;

  const form = useForm(
    z.object({
      text: (currentField && store.formControl.getSchema?.(currentField)) || z.string(),
    }),
  );

  const { fields } = form.root;

  useEffect(() => {
    if (store.state === 'INPUT') {
      fields.text.element?.focus();
    }
  }, []);

  useEffect(() => {
    pipe(fields.text.rawInput, (rawInput) => {
      store.setSearchText(rawInput as any);
    });
  }, [getDecodeResult(fields.text.decodeResult)]);

  return (
    <TextInput
      field={fields.text}
      onSelect={() => {
        pipe(fields.text.rawInput, (rawInput) => {
          store.setSearchText(rawInput as any);
        });
        store.endTag();
      }}
      variant="tagSearch"
      containerProps={{ style: { width: 150 } }}
      inputOptions={currentField ? store.fields[currentField]?.inputOptions : undefined}
    />
  );
});

export interface TagSearchInputProps {
  placeholder?: React.ReactNode;
  classes?: ClassMap<'wrapper'>;
  styles?: Record<'wrapper', React.CSSProperties>;
  controller: TagSearchInputFormControl;
}

export const TagSearchInput: React.FC<TagSearchInputProps> = observer(
  ({ placeholder, classes: customClasses, styles, controller }) => {
    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const store = useMemo(() => new TagSearchInputStore(controller), [controller]);

    const fields = controller.useControlFields?.();

    // Keep controls in sync with parent form.
    useMemo(() => {
      fields && store.updateControls(fields);
    }, [fields]);

    const searchTextFormRef = useRef<HTMLDivElement>(null);

    const inputAreaRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (store.state === 'SELECT_FIELD') {
        inputAreaRef.current?.focus();
      }
    }, [store.state]);

    return (
      <TagSearchInputContext.Provider value={store}>
        <ClickAwayListener onClickAway={() => store.clickAway()}>
          <div style={styles?.wrapper} className={customClasses?.wrapper}>
            <div
              tabIndex={-1}
              className={clsx(classes.tagSearchInputContainer, store.focused && classes.focused)}
              onFocus={() => store.setFocused(true)}
              onBlur={() => store.setFocused(false)}
            >
              <SearchIcon
                fontSize="small"
                className={clsx(
                  store.focused ? classes.textGray700 : classes.textGray500,
                  classes.selfCenter,
                )}
              />
              <div
                tabIndex={0}
                ref={inputAreaRef}
                className={clsx(
                  classes.tagSearchInputInputArea,
                  store.state === 'START' && classes.baseButton,
                )}
                onClick={() => {
                  if (store.state === 'START') {
                    store.clickInput();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing) return;

                  switch (e.key) {
                    case 'Enter':
                      if (store.state === 'INPUT') {
                        store.endTag();
                        e.preventDefault();
                        e.stopPropagation();
                      } else if (store.state === 'SELECT_FIELD') {
                        store.startTag(store.selectedAutoCompleteTag);
                        e.preventDefault();
                        e.stopPropagation();
                      }
                      break;
                    case 'ArrowUp':
                    case 'ArrowDown':
                      let nextFieldIndex: undefined | number;

                      const fieldKeys = Object.keys(store.fields);

                      const currentFieldIndex = fieldKeys.findIndex(
                        (k) => k === store.selectedAutoCompleteTag,
                      );

                      if (e.key === 'ArrowUp') {
                        nextFieldIndex = currentFieldIndex - 1;
                      } else if (e.key === 'ArrowDown') {
                        nextFieldIndex = currentFieldIndex + 1;
                      }

                      if (nextFieldIndex !== undefined) {
                        store.setSelectedAutoCompleteTag(
                          fieldKeys[(nextFieldIndex + fieldKeys.length) % fieldKeys.length],
                        );
                      }

                      e.preventDefault();
                      e.stopPropagation();
                      break;
                  }
                }}
              >
                <div
                  className={classes.tagSearchInputPlaceholder}
                  style={{
                    display: store.hasInput || store.state === 'INPUT' ? 'none' : undefined,
                  }}
                >
                  <div className={classes.preEllipsis}>{placeholder}</div>
                </div>

                <div className={classes.tagSearchInputInputAreaInner}>
                  {Object.keys(store.controls).map((name) => (
                    <TagSearchTag key={name} name={name} />
                  ))}

                  {store.state === 'INPUT' && store.currentField && (
                    <TagSearchTag name={store.currentField!} creating />
                  )}

                  <div style={{ flex: 1, height: '100%' }} ref={searchTextFormRef}>
                    {store.state === 'INPUT' && <SearchTextForm key={store.currentField} />}
                  </div>

                  <SearchTagAutoComplete anchorEl={searchTextFormRef.current} />
                </div>
              </div>
            </div>

            <div
              className={clsx(classes.textGray700, classes.smallerText)}
              style={{ paddingTop: 4, lineHeight: 1, marginBottom: -6 }}
            >
              {formStrings.tagSearchInputHint}
            </div>
          </div>
        </ClickAwayListener>
      </TagSearchInputContext.Provider>
    );
  },
);
